import React, { useEffect, useState, useRef, Fragment } from 'react';
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../ui/LatexRenderer';
export function DragDropFillRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.interactiveConfig || {};
  const template = config.template || ''; // "Een ___ deur"
  const blanks = config.blanks || []; // [{id: 0, correctAnswer: "houten"}]
  const dragOptions = config.dragOptions || []; // ["houten", "houte", ...]
  const prevValidateRef = useRef(false);
  // State for filled blanks: { blankId: optionValue }
  const [filledBlanks, setFilledBlanks] = useState<Record<string, string>>({});
  // Reset state when question changes
  useEffect(() => {
    setFilledBlanks({});
    prevValidateRef.current = false;
  }, [question.id]);
  // Reset validation flag when shouldValidate becomes false
  useEffect(() => {
    if (!shouldValidate) {
      prevValidateRef.current = false;
    }
  }, [shouldValidate]);
  useEffect(() => {
    // Ready when all blanks are filled
    const isReady = blanks.every((b: any) => filledBlanks[b.id]);
    onReadyChange?.(isReady);
  }, [filledBlanks, blanks, onReadyChange]);
  // Rising-edge detection for shouldValidate
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      onAnswer(JSON.stringify(filledBlanks));
    }
  }, [shouldValidate, filledBlanks, onAnswer]);
  const handleOptionClick = (option: string) => {
    // Find first empty blank
    const firstEmpty = blanks.find((b: any) => !filledBlanks[b.id]);
    if (firstEmpty) {
      setFilledBlanks(prev => ({
        ...prev,
        [firstEmpty.id]: option
      }));
    }
  };
  const handleBlankClick = (blankId: string) => {
    // Clear the blank
    const newFilled = {
      ...filledBlanks
    };
    delete newFilled[blankId];
    setFilledBlanks(newFilled);
  };
  // Parse template to render text and blanks
  const parts = template.split('___');
  return <div className="w-full max-w-2xl mx-auto flex flex-col gap-8" data-id="element-4761">
      {/* Sentence Area */}
      <div className="p-6 bg-indigo-50 rounded-2xl border-2 border-indigo-100 text-xl leading-loose flex flex-wrap items-center gap-2 justify-center" data-id="element-4762">
        {parts.map((part: string, index: number) => <Fragment key={index} data-id="element-4763">
            <span data-id="element-4764">
              <LatexRenderer content={part} data-id="element-4765" />
            </span>
            {index < parts.length - 1 && blanks[index] && <button onClick={() => handleBlankClick(blanks[index].id)} className={`
                  min-w-[100px] h-10 px-3 rounded-lg border-2 border-dashed transition-all flex items-center justify-center font-bold
                  ${filledBlanks[blanks[index].id] ? 'bg-white border-indigo-500 text-indigo-700 shadow-sm' : 'bg-white/50 border-slate-300 text-slate-400'}
                `} data-id="element-4766">

                {filledBlanks[blanks[index].id] || '?'}
              </button>}
          </Fragment>)}
      </div>

      {/* Options Area */}
      <div className="flex flex-wrap gap-3 justify-center" data-id="element-4767">
        {dragOptions.map((option: string, index: number) => {
        // Check if this option is already used
        const isUsed = Object.values(filledBlanks).includes(option);
        return <button key={index} onClick={() => !isUsed && handleOptionClick(option)} disabled={isUsed} className={`
                px-4 py-2 rounded-xl border-2 font-medium text-lg transition-all
                ${isUsed ? 'bg-slate-100 border-slate-200 text-slate-300 cursor-default' : 'bg-white border-slate-200 text-slate-700 hover:border-indigo-400 hover:shadow-md hover:-translate-y-0.5'}
              `} data-id="element-4768">

              <LatexRenderer content={option} data-id="element-4769" />
            </button>;
      })}
      </div>

      <p className="text-center text-slate-400 text-sm" data-id="element-4770">
        Klik op een woord om het in de zin te plaatsen
      </p>
    </div>;
}